import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Coin } from "@phosphor-icons/react";
import { useExamCategories } from "../../lib/examCategories";

export default function ExamsList() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { categories: examCategories, labelFor } = useExamCategories();

  useEffect(() => {
    api.get("/exams").then((r) => setExams(r.data)).finally(() => setLoading(false));
  }, []);

  // Only show category chips that actually have at least one exam
  const availableCategories = useMemo(() => {
    const present = new Set(exams.map((e) => e.category).filter(Boolean));
    return examCategories.filter((c) => present.has(c.slug));
  }, [exams, examCategories]);

  const filtered = useMemo(() => {
    if (categoryFilter === "all") return exams;
    return exams.filter((e) => e.category === categoryFilter);
  }, [exams, categoryFilter]);

  return (
    <div>
      <PageHeader overline="Catalog" title="Available exams" subtitle="Browse exams published for your institution." />

      {!loading && exams.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="exam-category-filters">
          <button
            onClick={() => setCategoryFilter("all")}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${categoryFilter === "all" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
            data-testid="exam-cat-filter-all"
          >
            All <span className="ml-1 font-mono opacity-70">{exams.length}</span>
          </button>
          {availableCategories.map((c) => {
            const count = exams.filter((e) => e.category === c.slug).length;
            const active = categoryFilter === c.slug;
            return (
              <button
                key={c.slug}
                onClick={() => setCategoryFilter(c.slug)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${active ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
                data-testid={`exam-cat-filter-${c.slug}`}
              >
                {c.label} <span className="ml-1 font-mono opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="exams-empty">
          {exams.length === 0 ? "No exams available yet." : "No exams in this category."}
        </div>
      ) : (
        <div className="border border-zinc-200" data-testid="exams-table">
          {/* Table header — hidden on mobile, shown from md+ */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1">Duration</div>
            <div className="col-span-2">Questions</div>
            <div className="col-span-1">Marks</div>
            <div className="col-span-1">Price</div>
            <div className="col-span-1 text-right">Open</div>
          </div>
          {filtered.map((e) => {
            const price = Number(e.price_credits || 0);
            const priceBadge = price > 0 ? (
              <span className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 text-xs font-semibold" data-testid={`exam-price-${e.exam_id}`}>
                <Coin size={12} weight="fill" className="text-amber-500" />
                <span className="font-mono">{price}</span> coins
              </span>
            ) : (
              <span className="inline-block border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-semibold" data-testid={`exam-price-${e.exam_id}`}>
                Free
              </span>
            );
            const catBadge = (
              <span
                className="inline-block border border-indigo-300 bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
                data-testid={`exam-category-badge-${e.exam_id}`}
              >
                {labelFor(e.category)}
              </span>
            );
            return (
              <Link
                key={e.exam_id}
                to={`/app/exam/${e.exam_id}`}
                className="block border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50"
                data-testid={`exam-row-${e.exam_id}`}
              >
                {/* Mobile card layout */}
                <div className="md:hidden p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{e.title}</div>
                      <div className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{e.description}</div>
                    </div>
                    <div className="flex-shrink-0">{priceBadge}</div>
                  </div>
                  <div className="mt-2">{catBadge}</div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-600">
                    <span><span className="uppercase tracking-wider text-[10px] text-zinc-400 mr-1">Dur</span><span className="font-mono-tab font-medium">{e.duration_minutes}m</span></span>
                    <span><span className="uppercase tracking-wider text-[10px] text-zinc-400 mr-1">Q</span><span className="font-mono-tab font-medium">{e.question_ids?.length || 0}</span></span>
                    <span><span className="uppercase tracking-wider text-[10px] text-zinc-400 mr-1">Marks</span><span className="font-mono-tab font-medium">{e.total_marks}</span></span>
                    <span className="ml-auto text-zinc-900 font-bold">→</span>
                  </div>
                </div>
                {/* Desktop row layout */}
                <div className="hidden md:grid grid-cols-12 px-5 py-4 items-center text-sm">
                  <div className="col-span-4">
                    <div className="font-medium">{e.title}</div>
                    <div className="text-xs text-zinc-500 line-clamp-1">{e.description}</div>
                  </div>
                  <div className="col-span-2">{catBadge}</div>
                  <div className="col-span-1 font-mono-tab">{e.duration_minutes}m</div>
                  <div className="col-span-2 font-mono-tab">{e.question_ids?.length || 0}</div>
                  <div className="col-span-1 font-mono-tab">{e.total_marks}</div>
                  <div className="col-span-1">{priceBadge}</div>
                  <div className="col-span-1 text-right text-zinc-900">→</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
